import {
  OPLOG_ENTRY_MAX_CHARS,
  sameJsonValue,
  type AnyEnvelope,
  type OpLogKind,
} from '@wiztime/core';
import type { ChangeEntry } from './undo/types';

/**
 * The device-local operation log (data dictionary §11): an append-only record of every user
 * operation, synced change and system operation, for audit and diagnosis. It never syncs and is
 * not part of the normal export.
 */

export const OpLogOp = {
  Create: 'create',
  Update: 'update',
  Delete: 'delete',
} as const;
export type OpLogOp = (typeof OpLogOp)[keyof typeof OpLogOp];

/** One affected record: only the fields that changed, as [before, after]. */
export interface OpLogChange {
  readonly id: string;
  readonly type: string;
  readonly op: OpLogOp;
  readonly fields: Readonly<Record<string, readonly [unknown, unknown]>>;
  /** Set when the values were dropped to keep the entry under OPLOG_ENTRY_MAX_CHARS. */
  readonly truncated?: true;
}

/** An entry as written; `seq` is assigned by storage. */
export interface OpLogInput {
  readonly at: string;
  readonly deviceId: string;
  readonly appVersion: string;
  readonly kind: OpLogKind;
  readonly name: string;
  readonly summary: Readonly<Record<string, unknown>>;
  readonly changes: readonly OpLogChange[];
}

export interface OpLogEntry extends OpLogInput {
  readonly seq: number;
}

export interface OpLogQuery {
  readonly kind?: OpLogKind;
  /** ISO instants, inclusive. */
  readonly from?: string;
  readonly to?: string;
  /** Newest first, at most this many. */
  readonly limit?: number;
}

/** Fields derived from others or too large to be useful in a diff. */
const SKIPPED_FIELDS = new Set(['revisions']);

function dataFields(record: AnyEnvelope | null): Record<string, unknown> {
  return record === null || record.data === null ? {} : (record.data as Record<string, unknown>);
}

/** The changed fields of one record write (data dictionary §11). */
export function opLogChangeOf(entry: ChangeEntry): OpLogChange {
  const before = dataFields(entry.before);
  const after = dataFields(entry.after);
  const op =
    entry.before === null || entry.before.data === null
      ? OpLogOp.Create
      : entry.after.data === null
        ? OpLogOp.Delete
        : OpLogOp.Update;
  const fields: Record<string, readonly [unknown, unknown]> = {};
  for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
    if (SKIPPED_FIELDS.has(key) || sameJsonValue(before[key] ?? null, after[key] ?? null)) continue;
    fields[key] = [before[key] ?? null, after[key] ?? null];
  }
  const change: OpLogChange = { id: entry.id, type: entry.after.type, op, fields };
  if (JSON.stringify(change).length <= OPLOG_ENTRY_MAX_CHARS) return change;
  return {
    ...change,
    fields: Object.fromEntries(Object.keys(fields).map((key) => [key, [null, null] as const])),
    truncated: true,
  };
}

/** The changes of a list of record writes, within the entry size bound as a whole. */
export function opLogChangesOf(entries: readonly ChangeEntry[]): OpLogChange[] {
  const changes = entries.map(opLogChangeOf);
  let size = 0;
  return changes.map((change) => {
    size += JSON.stringify(change).length;
    if (size <= OPLOG_ENTRY_MAX_CHARS) return change;
    return {
      ...change,
      fields: Object.fromEntries(Object.keys(change.fields).map((key) => [key, [null, null] as const])),
      truncated: true,
    };
  });
}
